'use client';

import { ContentIcon } from '@eliadmin/core/src/components/ui/content-icon';

import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

type Tab = { id: string; label: string; icon: string };

/** Záložky „Pre koho" – ARIA tabs so šípkami, Home a End (inaktívne záložky majú tabIndex −1). */
export function PartnersTabs({
  label,
  tabs,
  panels,
}: {
  label: string;
  tabs: Tab[];
  /** Obsah panelu ku každej záložke, v rovnakom poradí (vykreslený na serveri). */
  panels: ReactNode[];
}) {
  const [active, setActive] = useState(0);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    setActive(index);
    buttons.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const last = tabs.length - 1;
    const next = {
      ArrowRight: active === last ? 0 : active + 1,
      ArrowLeft: active === 0 ? last : active - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    select(next);
  };

  return (
    <div>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="mb-7 flex flex-wrap gap-[6px]"
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={element => {
              buttons.current[index] = element;
            }}
            id={`tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-controls={`panel-${tab.id}`}
            tabIndex={index === active ? 0 : -1}
            onClick={() => setActive(index)}
            className={cn(
              'inline-flex cursor-pointer appearance-none items-center gap-[10px] border border-solid px-5 py-[14px] text-[15px] font-semibold',
              index === active
                ? 'border-[#161412] bg-[#161412] text-white'
                : 'border-[#161412]/10 bg-white text-[#161412]',
            )}
          >
            <ContentIcon
              aria-hidden="true"
              className={cn(tab.icon, index === active ? 'text-accent' : 'text-primary')}
            />
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab, index) => (
        <div
          key={tab.id}
          id={`panel-${tab.id}`}
          role="tabpanel"
          aria-labelledby={`tab-${tab.id}`}
          tabIndex={0}
          className={cn(
            'mb-5 grid-cols-1 gap-8 border border-solid border-[#161412]/10 bg-white p-7 min-[1000px]:grid-cols-[1.1fr_.9fr] min-[1000px]:gap-14 min-[1000px]:p-11 [&>*]:min-w-0',
            index === active ? 'grid' : 'hidden',
          )}
        >
          {panels[index]}
        </div>
      ))}
    </div>
  );
}
